import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * AdminGraduationToaster — invisible component (renders null) that
 * polls `/admin/recent-graduations` every 30 seconds and fires a sonner
 * toast for every new "first post" by an owner. Mounted at the top of
 * the Admin page so it only runs when an admin is actually viewing.
 *
 * Dedup strategy: we track every shown owner_id in a ref-set, AND we
 * pass the server-echoed `as_of` timestamp back as the next `since`
 * cursor — so the server only sends rows newer than the last successful
 * poll, regardless of FE/server clock drift.
 */
export default function AdminGraduationToaster({ enabled = true, intervalMs = 30000 }) {
  const sinceRef = useRef(new Date().toISOString());
  const seenRef = useRef(new Set());
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) return undefined;

    const tick = async () => {
      if (!aliveRef.current) return;
      try {
        const since = encodeURIComponent(sinceRef.current);
        const { data } = await api.get(`/admin/recent-graduations?since=${since}`);
        if (!aliveRef.current) return;
        if (data?.as_of) sinceRef.current = data.as_of;
        const items = Array.isArray(data?.items) ? data.items : [];
        for (const it of items) {
          if (!it.owner_id || seenRef.current.has(it.owner_id)) continue;
          seenRef.current.add(it.owner_id);
          const truck = it.truck_name || it.name || "An owner";
          const proPrefix = it.is_pro ? "Pro · " : "";
          toast.success(`🎉 ${proPrefix}${truck} just posted their first stop`, {
            description: it.email
              ? `${it.email} — graduated from the activation funnel`
              : "Graduated from the activation funnel",
            duration: 10000,
            id: `graduation-${it.owner_id}`, // dedup against rapid double-poll
          });
        }
      } catch {
        /* swallow — we'll retry on the next interval */
      }
    };

    // Fire once immediately so the admin doesn't wait 30s to see anything.
    tick();
    const t = setInterval(tick, intervalMs);
    return () => {
      aliveRef.current = false;
      clearInterval(t);
    };
  }, [enabled, intervalMs]);

  return null;
}
